import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getTours } from './tours';
import { Button } from '../ui/button';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const TOOLTIP_W = 320;

export default function GuidedWalkthrough() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [tour, setTour] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null); // spotlight rect (viewport coords) or null = centered
  const [ready, setReady] = useState(false);

  const step = tour?.steps?.[stepIndex] || null;

  // Launch a tour on request
  useEffect(() => {
    const handler = (e) => {
      const id = e.detail?.tourId;
      const t = getTours(user?.role).find((x) => x.id === id);
      if (t) {
        setTour(t);
        setStepIndex(0);
      }
    };
    window.addEventListener('bbc:start-tour', handler);
    return () => window.removeEventListener('bbc:start-tour', handler);
  }, [user?.role]);

  const close = useCallback(() => {
    setTour(null);
    setStepIndex(0);
    setRect(null);
    setReady(false);
  }, []);

  // Resolve the current step: navigate, open menu, locate the target
  useEffect(() => {
    if (!tour || !step) return;
    let cancelled = false;
    setReady(false);
    setRect(null);

    (async () => {
      if (step.route && location.pathname !== step.route) {
        navigate(step.route);
        await wait(550);
      }

      let el = null;
      for (let i = 0; i < 26 && !cancelled; i++) {
        el = step.selector ? document.querySelector(step.selector) : null;
        if (el) break;
        if (step.expandMenu && i === 2) {
          const toggle = document.querySelector(`[data-testid="nav-${step.expandMenu}"]`);
          if (toggle) toggle.click();
        }
        await wait(150);
      }
      if (cancelled) return;

      if (el) {
        try {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (_) {}
        await wait(320);
        if (cancelled) return;
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else {
        setRect(null);
      }
      setReady(true);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour, stepIndex]);

  // Keep the spotlight aligned on scroll / resize
  useEffect(() => {
    if (!step?.selector) return;
    const update = () => {
      const el = document.querySelector(step.selector);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [step]);

  if (!tour || !step) return null;

  const isLast = stepIndex >= tour.steps.length - 1;
  const next = () => (isLast ? close() : setStepIndex((i) => i + 1));
  const back = () => stepIndex > 0 && setStepIndex((i) => i - 1);

  // Tooltip placement
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
  let tipStyle;
  if (rect) {
    const pad = 12;
    const spaceBelow = vh - (rect.top + rect.height);
    const placeBelow = spaceBelow > 220 || rect.top < 220;
    const top = placeBelow
      ? Math.min(rect.top + rect.height + pad, vh - 200)
      : Math.max(rect.top - pad - 190, 12);
    let left = rect.left + rect.width / 2 - TOOLTIP_W / 2;
    left = Math.max(12, Math.min(left, vw - TOOLTIP_W - 12));
    tipStyle = { top, left, width: TOOLTIP_W };
  } else {
    tipStyle = {
      top: '50%',
      left: '50%',
      width: Math.min(TOOLTIP_W, vw - 24),
      transform: 'translate(-50%, -50%)',
    };
  }

  return (
    <div className="fixed inset-0 z-[100] pointer-events-none" data-testid="guided-walkthrough">
      {/* Spotlight (or full dim when no target) */}
      {rect ? (
        <div
          className="absolute rounded-lg transition-all duration-300"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            boxShadow: '0 0 0 9999px rgba(15,23,42,0.55)',
            border: '2px solid rgba(255,255,255,0.9)',
          }}
        />
      ) : (
        <div className="absolute inset-0" style={{ background: 'rgba(15,23,42,0.55)' }} />
      )}

      {/* Tooltip card */}
      <div
        className="absolute pointer-events-auto bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-100 dark:border-gray-800 p-4"
        style={tipStyle}
        data-testid="walkthrough-tooltip"
      >
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-600 bg-blue-50 rounded-full px-2 py-0.5">
              {tour.title}
            </span>
          </div>
          <button
            type="button"
            onClick={close}
            className="text-gray-400 hover:text-gray-700 transition-colors"
            aria-label="Close walkthrough"
            data-testid="walkthrough-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <h4 className="font-semibold text-sm text-gray-900 dark:text-gray-100" data-testid="walkthrough-title">
          {step.title}
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 leading-relaxed" data-testid="walkthrough-body">
          {step.body}
        </p>

        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-muted-foreground" data-testid="walkthrough-progress">
            Step {stepIndex + 1} of {tour.steps.length}
          </span>
          <div className="flex items-center gap-2">
            {stepIndex > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={back}
                className="h-8 px-2"
                data-testid="walkthrough-back"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
            )}
            <Button
              size="sm"
              onClick={next}
              disabled={!ready}
              className="h-8 bg-blue-600 hover:bg-blue-700"
              data-testid="walkthrough-next"
            >
              {isLast ? 'Done' : 'Next'}
              {!isLast && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
